<template>
  <div class="sp-finder-result-thumbnail" part="base">
    <div class="sp-finder-result-thumbnail__frame" part="frame">
      <span v-if="hasGroupedImages" class="sp-finder-result-thumbnail__frame-group" part="group">
        <span
          v-for="(physicianImageUrl, index) in visiblePhysicianImageUrls"
          :key="`${physicianImageUrl}-${index}`"
          class="sp-finder-result-thumbnail__image-parent"
        >
          <sp-img class="sp-finder-result-thumbnail__image" :src="physicianImageUrl" :alt="groupImageAlt"></sp-img>
        </span>
      </span>

      <a v-else-if="hasImageLink" class="sp-finder-result-thumbnail__image-parent" :href="url" part="image-link">
        <sp-img class="sp-finder-result-thumbnail__image" :src="imageUrl" :alt="imageAlt"></sp-img>
      </a>

      <span v-else class="sp-finder-result-thumbnail__image-parent" part="image">
        <sp-img class="sp-finder-result-thumbnail__image" :src="imageUrl" :alt="imageAlt"></sp-img>
      </span>

      <span v-if="badgeLabel" class="sp-finder-result-thumbnail__badge" part="badge">
        <sp-img
          v-if="badge?.icon_url"
          class="sp-finder-result-thumbnail__badge-icon"
          :src="badge.icon_url"
          alt=""
        ></sp-img>
        <span class="sp-finder-result-thumbnail__badge-text">{{ badgeLabel }}</span>
      </span>
    </div>

    <span v-if="physicianCountLabel" class="sp-finder-result-thumbnail__count" part="count">
      {{ physicianCountLabel }}
    </span>

    <div v-if="visibleAwards.length" class="sp-finder-result-thumbnail__awards" part="awards">
      <a
        v-for="award in visibleAwards"
        :key="awardKey(award)"
        class="sp-finder-result-thumbnail__award"
        :href="award.external_url"
        target="_blank"
        rel="noopener noreferrer"
      >
        <img class="sp-finder-result-thumbnail__award-icon" :src="award.thumbnail_url" :alt="award.name" />
      </a>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { i18n } from "#ce/components/SpFinder/i18n";

// Direct instance access (see SpFinderResult.ce.vue) — useI18n() can't resolve
// the plugin across the custom-element app boundary.
function t(...args) {
  return i18n.global.t(...args);
}

const props = defineProps({
  resultType: {
    type: String,
    default: "provider",
    validator: (value) => ["provider", "location"].includes(value),
  },
  name: {
    type: String,
    default: "",
  },
  url: {
    type: String,
    default: undefined,
  },
  imageUrl: {
    type: String,
    default: "",
  },
  physicianImageUrls: {
    type: Array,
    default: () => [],
  },
  physicianCountLabel: {
    type: String,
    default: "",
  },
  badge: {
    type: Object,
    default: null,
  },
  independentBadgeText: {
    type: String,
    default: "",
  },
  awards: {
    type: Array,
    default: () => [],
  },
});

const isLocation = computed(() => props.resultType === "location");
const visiblePhysicianImageUrls = computed(() => props.physicianImageUrls.slice(0, 4));
const visibleAwards = computed(() => props.awards.filter((award) => award.thumbnail_url));

const hasGroupedImages = computed(() => visiblePhysicianImageUrls.value.length > 0);
const hasImageLink = computed(() => Boolean(props.url && !isLocation.value));

const groupImageAlt = computed(() => t("provider_group_alt", { name: props.name }));
const imageAlt = computed(() =>
  isLocation.value ? t("location_alt", { name: props.name }) : t("provider_alt", { name: props.name }),
);
const badgeLabel = computed(() => props.badge?.text || props.independentBadgeText);

function awardKey(award) {
  return award.id ?? award.thumbnail_url;
}
</script>

<style>
:host {
  display: block;
}
</style>

<style lang="scss" scoped>
.sp-finder-result-thumbnail {
  /* Layout */
  --thumbnail-gap: var(--sp-comp-finder-result-thumbnail-gap, var(--sp-ref-scale-8));
  --thumbnail-aspect-ratio: var(--sp-comp-finder-result-thumbnail-aspect-ratio, 1);

  /* Image */
  --thumbnail-background: var(--sp-comp-finder-result-thumbnail-background, var(--sp-sys-color-transparent));
  --thumbnail-border-radius: var(--sp-comp-finder-result-thumbnail-border-radius, 50%);

  /* Group */
  --thumbnail-group-image-size: var(--sp-comp-finder-result-thumbnail-group-image-size, 70%);
  --thumbnail-group-overlap: var(--sp-comp-finder-result-thumbnail-group-overlap, 60%);
  --thumbnail-group-border: var(--sp-comp-finder-result-thumbnail-group-border, 3px solid var(--sp-sys-color-surface));
  --thumbnail-group-shadow: var(--sp-comp-finder-result-thumbnail-group-shadow, var(--sp-sys-elevation-2));

  /* Badge */
  --thumbnail-badge-display: var(--sp-comp-finder-result-thumbnail-badge-display, inline-flex);
  --thumbnail-badge-background: var(--sp-comp-finder-result-thumbnail-badge-background, var(--sp-sys-color-primary));
  --thumbnail-badge-color: var(--sp-comp-finder-result-thumbnail-badge-color, var(--sp-sys-color-on-primary));
  --thumbnail-badge-size: var(--sp-comp-finder-result-thumbnail-badge-size, 2rem);

  /* Count */
  --thumbnail-count-display: var(--sp-comp-finder-result-thumbnail-count-display, inline);
  --thumbnail-count-color: var(--sp-comp-finder-result-thumbnail-count-color, var(--sp-sys-color-on-surface));

  /* Awards */
  --thumbnail-award-size: var(--sp-comp-finder-result-thumbnail-award-size, 2.5rem);
  --thumbnail-awards-gap: var(--sp-comp-finder-result-thumbnail-awards-gap, var(--sp-ref-scale-6));

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--thumbnail-gap);
  width: 100%;
  max-width: 100%;

  &__frame {
    position: relative;
    width: 100%;
    aspect-ratio: var(--thumbnail-aspect-ratio);
    background: var(--thumbnail-background);
  }

  &__frame-group {
    display: flex;
    align-items: flex-start;
    justify-content: flex-end;
    height: 100%;
  }

  &__frame-group > &__image-parent {
    flex: 0 0 var(--thumbnail-group-image-size);
    height: var(--thumbnail-group-image-size);
    border: var(--thumbnail-group-border);
    box-shadow: var(--thumbnail-group-shadow);
  }

  &__frame-group > &__image-parent + &__image-parent {
    margin-left: calc(var(--thumbnail-group-overlap) * -1);
  }

  &__image-parent {
    display: block;
    width: 100%;
    height: 100%;
    overflow: hidden;
    border-radius: var(--thumbnail-border-radius);
    background: var(--thumbnail-background);
    color: inherit;
    text-decoration: none;
  }

  &__image,
  &__image::part(base) {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__badge {
    position: absolute;
    right: var(--sp-ref-scale-8);
    bottom: var(--sp-ref-scale-8);
    display: var(--thumbnail-badge-display);
    align-items: center;
    justify-content: center;
    min-width: var(--thumbnail-badge-size);
    min-height: var(--thumbnail-badge-size);
    box-sizing: border-box;
    padding: 0 var(--sp-ref-scale-8);
    border-radius: var(--thumbnail-badge-size);
    background: var(--thumbnail-badge-background);
    color: var(--thumbnail-badge-color);
    font: var(--sp-sys-typescale-small-bold-short, inherit);
    box-shadow: var(--sp-sys-elevation-2, none);
  }

  &__badge-icon {
    width: calc(var(--thumbnail-badge-size) * 0.75);
    height: calc(var(--thumbnail-badge-size) * 0.75);
  }

  &__badge-text {
    max-width: 8rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__count {
    display: var(--thumbnail-count-display);
    color: var(--thumbnail-count-color);
    font: var(--sp-sys-typescale-small-short, inherit);
  }

  &__awards {
    display: flex;
    flex-wrap: wrap;
    gap: var(--thumbnail-awards-gap);
  }

  &__award {
    display: inline-flex;
  }

  &__award-icon {
    width: var(--thumbnail-award-size);
    height: var(--thumbnail-award-size);
    object-fit: contain;
  }
}
</style>
